// src/features/task/TaskNumberingAdmin.tsx
// Admin: task numbering schemes (e.g. MP-123, BACK-42).
// Wraps List/Create/Update/Delete/GetTaskNumberingScheme.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTaskTaskNumbering, useI18n } from '@react/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Input, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useScope } from '@/hooks/useScope';
import { formatEnumLabel } from '@/features/task/logic';
import { NUMBERING_PATTERNS, NUMBERING_RESET_STRATEGIES, digitsOnly, diffFields, makeEntity, previewNumberingSample } from '@/features/task/panelHelpers';
import type { TaskNumberingScheme } from '@core/types/task';

const EDITABLE_FIELDS: (keyof TaskNumberingScheme)[] = [
  'name', 'prefix', 'separator', 'pattern', 'counter_digits', 'suffix', 'format_template', 'reset_strategy', 'is_active',
];

interface EditFormState {
  name: string;
  prefix: string;
  separator: string;
  pattern: string;
  digits: string;
  suffix: string;
  format_template: string;
  reset_strategy: string;
  is_active: boolean;
}

export const TaskNumberingAdmin = observer(() => {
  const store = useTaskTaskNumbering();
  const { t } = useI18n();
  const scope = useScope();

  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [separator, setSeparator] = useState('-');
  const [pattern, setPattern] = useState<string>(NUMBERING_PATTERNS[0]);
  const [digits, setDigits] = useState('4');
  const [suffix, setSuffix] = useState('');
  const [formatTemplate, setFormatTemplate] = useState('');
  const [reset, setReset] = useState<string>(NUMBERING_RESET_STRATEGIES[0]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => { store.ListTaskNumberingSchemes(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const createPreview = previewNumberingSample({ prefix, separator, counter_digits: parseInt(digits || '1', 10), suffix, format_template: formatTemplate });

  const create = async () => {
    if (!name.trim() || !prefix.trim()) return;
    try {
      await store.CreateTaskNumberingScheme({
        entity: makeEntity('', 'task_numbering_scheme', scope),
        name: name.trim(),
        prefix: prefix.trim(),
        separator,
        pattern: pattern as any,
        counter_digits: parseInt(digits || '4', 10),
        suffix: suffix.trim() || undefined,
        format_template: formatTemplate.trim() || undefined,
        reset_strategy: reset as any,
      } as any);
      setName(''); setPrefix(''); setSeparator('-'); setDigits('4'); setSuffix(''); setFormatTemplate('');
      setPattern(NUMBERING_PATTERNS[0]); setReset(NUMBERING_RESET_STRATEGIES[0]);
    } catch { /* store.error */ }
  };

  const startEdit = async (id: string) => {
    setEditingId(id);
    try {
      const s = await store.GetTaskNumberingScheme(id);
      setEditForm({
        name: s?.name || '',
        prefix: s?.prefix || '',
        separator: s?.separator ?? '-',
        pattern: (s?.pattern as string) || NUMBERING_PATTERNS[0],
        digits: String(s?.counter_digits ?? 4),
        suffix: s?.suffix || '',
        format_template: s?.format_template || '',
        reset_strategy: (s?.reset_strategy as string) || NUMBERING_RESET_STRATEGIES[0],
        is_active: s?.is_active ?? true,
      });
    } catch {
      setEditForm(null);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async (id: string) => {
    if (!editForm) return;
    const current = store.items.find((s) => s.entity?.id === id) || null;
    const updated: TaskNumberingScheme = {
      ...(current || ({} as TaskNumberingScheme)),
      name: editForm.name.trim(),
      prefix: editForm.prefix.trim(),
      separator: editForm.separator,
      pattern: editForm.pattern as TaskNumberingScheme['pattern'],
      counter_digits: parseInt(editForm.digits || '4', 10),
      suffix: editForm.suffix.trim() || undefined,
      format_template: editForm.format_template.trim() || undefined,
      reset_strategy: editForm.reset_strategy as TaskNumberingScheme['reset_strategy'],
      is_active: editForm.is_active,
    };
    const paths = diffFields(current, updated, EDITABLE_FIELDS);
    if (paths.length === 0) { cancelEdit(); return; }
    try {
      await store.UpdateTaskNumberingScheme(id, { scheme: updated, field_mask: paths } as any);
      cancelEdit();
    } catch { /* store.error */ }
  };

  const remove = async (id: string) => {
    try { await store.DeleteTaskNumberingScheme(id); setConfirmId(null); } catch { /* store.error */ }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.numbering.title')}</CardTitle>
        <CardDescription>{t('task.numbering.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

        {store.isLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}</div>
        ) : store.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.numbering.empty')}</p>
        ) : (
          <div className="space-y-2">
            {store.items.map((s) => {
              const id = s.entity?.id || '';
              const isEditing = editingId === id;
              const sample = previewNumberingSample(s);
              const editPreview = isEditing && editForm
                ? previewNumberingSample({ prefix: editForm.prefix, separator: editForm.separator, counter_digits: parseInt(editForm.digits || '1', 10), suffix: editForm.suffix, format_template: editForm.format_template, next_number: s.next_number })
                : sample;
              return (
                <div key={id} className="rounded-md border border-border">
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{s.name}</span>
                      <Badge variant="secondary" size="sm">{isEditing ? editPreview : sample}</Badge>
                      {!s.is_active && <Badge variant="secondary" size="sm">{t('common.inactive')}</Badge>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isEditing ? (
                        <>
                          <Button size="sm" isLoading={store.isSaving} onClick={() => saveEdit(id)}>{t('common.save')}</Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit}>{t('common.cancel')}</Button>
                        </>
                      ) : confirmId === id ? (
                        <>
                          <Button size="sm" variant="danger" isLoading={store.isDeleting} onClick={() => remove(id)}>{t('common.confirm')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>{t('common.cancel')}</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(id)}>{t('common.edit')}</Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmId(id)}>{t('common.delete')}</Button>
                        </>
                      )}
                    </div>
                  </div>

                  {isEditing && editForm && (
                    <div className="space-y-3 border-t border-border px-3 py-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Input label={t('task.numbering.name')} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                        <Input label={t('task.numbering.prefix')} value={editForm.prefix} onChange={(e) => setEditForm({ ...editForm, prefix: e.target.value.toUpperCase() })} />
                        <Input label={t('task.numbering.digits')} value={editForm.digits} onChange={(e) => setEditForm({ ...editForm, digits: digitsOnly(e.target.value) })} inputMode="numeric" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Input label={t('task.numbering.separator')} value={editForm.separator} onChange={(e) => setEditForm({ ...editForm, separator: e.target.value })} />
                        <Input label={t('task.numbering.suffix')} value={editForm.suffix} onChange={(e) => setEditForm({ ...editForm, suffix: e.target.value })} />
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.numbering.pattern')}</label>
                          <Select value={editForm.pattern} onChange={(e) => setEditForm({ ...editForm, pattern: e.target.value })}>
                            {NUMBERING_PATTERNS.map((p) => <option key={p} value={p}>{formatEnumLabel(p, 'TASK_NUMBERING_PATTERN_')}</option>)}
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.numbering.reset')}</label>
                          <Select value={editForm.reset_strategy} onChange={(e) => setEditForm({ ...editForm, reset_strategy: e.target.value })}>
                            {NUMBERING_RESET_STRATEGIES.map((r) => <option key={r} value={r}>{formatEnumLabel(r, 'TASK_NUMBERING_RESET_STRATEGY_')}</option>)}
                          </Select>
                        </div>
                        <ToggleSwitch label={t('common.active')} checked={editForm.is_active} onChange={(v) => setEditForm({ ...editForm, is_active: v })} />
                      </div>
                      <Input
                        label={t('task.numbering.formatTemplate')}
                        value={editForm.format_template}
                        onChange={(e) => setEditForm({ ...editForm, format_template: e.target.value })}
                        placeholder="{prefix}{separator}{counter}"
                      />
                      <p className="text-xs text-muted-foreground">{t('task.numbering.previewLabel')}: <span className="font-mono text-foreground">{editPreview}</span></p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground">{t('task.numbering.addNew')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input label={t('task.numbering.name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('task.numbering.namePlaceholder')} />
            <Input label={t('task.numbering.prefix')} value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} placeholder="MP" />
            <Input label={t('task.numbering.digits')} value={digits} onChange={(e) => setDigits(digitsOnly(e.target.value))} inputMode="numeric" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input label={t('task.numbering.separator')} value={separator} onChange={(e) => setSeparator(e.target.value)} placeholder="-" />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.numbering.pattern')}</label>
              <Select value={pattern} onChange={(e) => setPattern(e.target.value)}>
                {NUMBERING_PATTERNS.map((p) => <option key={p} value={p}>{formatEnumLabel(p, 'TASK_NUMBERING_PATTERN_')}</option>)}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.numbering.reset')}</label>
              <Select value={reset} onChange={(e) => setReset(e.target.value)}>
                {NUMBERING_RESET_STRATEGIES.map((r) => <option key={r} value={r}>{formatEnumLabel(r, 'TASK_NUMBERING_RESET_STRATEGY_')}</option>)}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label={t('task.numbering.suffix')} value={suffix} onChange={(e) => setSuffix(e.target.value)} />
            <Input label={t('task.numbering.formatTemplate')} value={formatTemplate} onChange={(e) => setFormatTemplate(e.target.value)} placeholder="{prefix}{separator}{counter}" />
          </div>
          <p className="text-xs text-muted-foreground">{t('task.numbering.previewLabel')}: <span className="font-mono text-foreground">{createPreview}</span></p>
          <div className="flex justify-end">
            <Button onClick={create} disabled={!name.trim() || !prefix.trim()} isLoading={store.isSaving}>{t('task.numbering.add')}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default TaskNumberingAdmin;
